import {
  IBasePicker,
  IBasePickerStyleProps,
  IBasePickerStyles,
  IBasePickerSuggestionsProps,
  ICommandBarStyles,
  IInputProps,
  IPickerItemProps,
  IStyleFunctionOrObject,
  ISuggestionItemProps,
  ITag,
  TagPicker,
  ValidationState,
} from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPCommandBarButton } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import CreateTagCallOut from 'components/CallOuts/CreateTagCallOut';
import { KeyValueTag } from 'components/Tags/KeyValueTag';
import { globalSuggestionsMaxRecords } from 'globalConstants';
import { newIcon } from 'globalStyles';
import Tag from 'models/tag';
import { createRef, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sortOnTag } from 'utils/sorting';

interface IKeyValueTagPickerProps {
  selectedTags: Tag[];
  itemLimit?: number;
  disabled?: boolean;
  onRemove: (item: Tag) => void;
  onAdd: (item: Tag) => void;
  onCreate?: (item: Tag) => void;
  isLoading?: boolean;
  allowCreate?: boolean;
  styles?: IStyleFunctionOrObject<IBasePickerStyleProps, IBasePickerStyles> | undefined;
  maxTagWidth?: number;
  allowedTags?: Tag[];
  disallowedTags?: Tag[];
  hideCategory?: boolean; //if true, the category will not be shown in the suggestions
}

const KeyValueTagPicker = (props: IKeyValueTagPickerProps) => {
  const { t } = useTranslation(['translation', 'tag']);
  const appContext = useContext(AppContext);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<Tag[] | undefined>(props.selectedTags);
  const [createNewItem, setCreateNewItem] = useState<boolean>(false);
  const [filter, setFilter] = useState<string | undefined>(undefined);
  const tagPickerRef = createRef<IBasePicker<ITag>>();
  const calloutId = useMemo(() => 'tag-picker-' + Math.random().toString(36).substring(7), []);

  useEffect(() => {
    if (props.allowedTags !== undefined) {
      setAllTags(props.allowedTags);
      setTags(removeDisallowedTags(props.allowedTags, props.disallowedTags));
    } else {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.allowedTags, props.disallowedTags]);

  const loadData = async () => {
    const tags = await appContext.globalDataCache.tags.getItems();
    setAllTags(tags);
    setTags(removeDisallowedTags(tags, props.disallowedTags));
  };

  const removeDisallowedTags = (tags: Tag[], disallowedTags: Tag[] | undefined): Tag[] => {
    if (disallowedTags === undefined) {
      return tags;
    }

    return tags.filter((tag) => !disallowedTags?.some((disTag) => disTag.tagId === tag.tagId));
  };

  useEffect(() => {
    //set selected tags in state so they can be modified
    setSelectedTags(props.selectedTags);
  }, [props.selectedTags]);

  const buttonStyles: Partial<ICommandBarStyles> = {
    root: {
      height: 30,
    },
  };

  const getITag = (tag: Tag): ITag => {
    return {
      name: tag.value(),
      key: tag.tagId.toString(),
    };
  };

  const getITags = (tags: Tag[] | undefined): ITag[] | undefined => {
    return tags?.map((t) => getITag(t));
  };

  const onResolveSuggestions = async (filter: string, selectedItems: ITag[] | undefined): Promise<ITag[]> => {
    if (filter) setFilter(filter);

    if (filter && tags) {
      const filterLower = filter.toLowerCase();

      return tags
        .filter(
          (tag: Tag) =>
            !selectedTags?.some((t) => t.tagId === tag.tagId) &&
            (tag.tagName.toLowerCase().includes(filterLower) || tag.tagValue.toLowerCase().includes(filterLower)),
        )
        .sort((a, b) => {
          return sortOnTag(a, b);
        })
        .map((tag: Tag): ITag => {
          return getITag(tag);
        });
    }

    return [];
  };

  const onRenderCreateNewTag = () => {
    return (
      <IPCommandBarButton
        iconProps={newIcon}
        styles={buttonStyles}
        text={t('tag:Picker.Add.Button')}
        onClick={() => {
          tagPickerRef.current?.completeSuggestion(true);
          setCreateNewItem(true);
        }}
      />
    );
  };

  const suggestionProps: IBasePickerSuggestionsProps = {
    showRemoveButtons: false,
    resultsMaximumNumber: globalSuggestionsMaxRecords,
    noResultsFoundText: t('translation:General.Suggestions.NoData'),
    onRenderNoResultFound: props.onCreate && props.allowCreate ? onRenderCreateNewTag : undefined,
  };

  const removeSelectedItem = (tag: Tag) => {
    //remove from selected items
    const items = selectedTags?.filter((t) => t.tagId !== tag.tagId);
    setSelectedTags(items);
    if (props.onRemove) {
      props.onRemove(tag);
    }
  };

  const onRenderItem = (pickerProps: IPickerItemProps<ITag>): JSX.Element => {
    const tagItem = pickerProps.item;
    if (!selectedTags) return <span />;

    const tag = selectedTags.find((t) => t.tagId.toString() === tagItem.key);
    if (!tag) return <span />;

    return <KeyValueTag key={tag.tagId} tag={tag} maxWidth={props.maxTagWidth} onRemove={removeSelectedItem} />;
  };

  const onRenderSuggestionsItem = (pickerProps: ITag, itemProps: ISuggestionItemProps<ITag>): JSX.Element => {
    const tagItem = pickerProps;
    if (!tags) return <span />;

    const tag = tags.find((t) => t.tagId.toString() === tagItem.key);
    if (!tag) return <span />;

    return <KeyValueTag tag={tag} showCategory={props.hideCategory === true ? false : true} />;
  };

  const onSelectItem = (item?: ITag | undefined) => {
    if (item) {
      let id: string;
      if (!item.key && props.allowCreate) {
        setCreateNewItem(true);

        return null;
      } else {
        id = item.key as string;
      }

      const tag = tags?.find((t) => t.tagId.toString() === id);
      if (tag) {
        const newSelectedTags = selectedTags?.slice(0) || [];
        newSelectedTags.push(tag);
        setSelectedTags(newSelectedTags);
        if (props.onAdd) {
          props.onAdd(tag);
        }
      }

      return item;
    }

    return null;
  };

  const tagAlreadyExists = (tag: Tag): boolean => {
    const nameLower = tag.tagName.toLowerCase();
    const valueLower = tag.tagValue.toLowerCase();

    return (
      selectedTags?.some((t) => t.tagName.toLowerCase() === nameLower && t.tagValue.toLowerCase() === valueLower) ??
      false
    );
  };

  const onCreateItem = (tag: Tag) => {
    if (tagAlreadyExists(tag)) {
      return;
    }
    //add to selected items
    const newSelectedTags = selectedTags?.slice(0) || [];
    newSelectedTags.push(tag);
    setSelectedTags(newSelectedTags);
    if (props.onCreate) {
      props.onCreate(tag);
    }
    //add to all tags
    const newAllTags = allTags.slice(0);
    newAllTags.push(tag);
    setAllTags(newAllTags);
  };

  const inputProps: IInputProps = {
    placeholder: props.allowCreate ? t('tag:Picker.Placeholder') : t('tag:Picker.PlaceholderNoCreate'),
  };

  const onEmptyResolveSuggestions = (selectedItems?: ITag[] | undefined): ITag[] => {
    return tags
      .filter((tag: Tag) => !selectedItems?.some((t) => t.key.toString() === tag.tagId.toString()))
      .sort((a, b) => {
        return sortOnTag(a, b);
      })
      .slice(0, 50)
      .map((tag: Tag): ITag => {
        return getITag(tag);
      });
  };

  //
  // Main render
  //
  return (
    <IPStack id={calloutId}>
      <TagPicker
        componentRef={tagPickerRef}
        itemLimit={props.itemLimit}
        onResolveSuggestions={onResolveSuggestions}
        pickerSuggestionsProps={suggestionProps}
        selectedItems={getITags(selectedTags)}
        onRenderSuggestionsItem={onRenderSuggestionsItem}
        onEmptyResolveSuggestions={onEmptyResolveSuggestions}
        disabled={props.disabled}
        onRenderItem={onRenderItem}
        createGenericItem={(input: string) => {
          return { key: '', name: input } as ITag;
        }}
        onItemSelected={onSelectItem}
        onValidateInput={(input: string) => {
          return input ? ValidationState.valid : ValidationState.invalid;
        }}
        inputProps={inputProps}
        styles={props.styles}
      />
      {createNewItem &&
        allTags && ( //to destroy the state of the call-out
          <CreateTagCallOut
            tagCache={allTags}
            targetId={calloutId}
            onClose={() => setCreateNewItem(false)}
            onCreate={onCreateItem}
            isVisible={createNewItem}
            defaultGroup={filter}
          />
        )}
    </IPStack>
  );
};

export default KeyValueTagPicker;
